import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CallRingLevel {
  id: string;
  level: number;
  name: string;
  timeout_seconds: number;
  user_ids: string[];
  active: boolean;
  business_unit: string;
}

export function useCallRingSettings(businessUnit: 'br' | 'us' = 'br') {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['call-ring-settings', businessUnit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('call_ring_settings' as any)
        .select('*')
        .eq('business_unit', businessUnit)
        .order('level');

      if (error) throw error;
      return data as unknown as CallRingLevel[];
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (levels: Partial<CallRingLevel>[]) => {
      const promises = levels.map((level) =>
        supabase
          .from('call_ring_settings' as any)
          .upsert({
            level: level.level!,
            name: level.name!,
            timeout_seconds: level.timeout_seconds!,
            user_ids: level.user_ids!,
            active: level.active ?? true,
            business_unit: businessUnit,
          }, {
            onConflict: 'level,business_unit'
          })
          .select()
      );

      const results = await Promise.all(promises);
      const errors = results.filter((r) => r.error);
      
      if (errors.length > 0) {
        throw errors[0].error;
      }

      return results.map((r) => r.data).flat();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-ring-settings', businessUnit] });
      toast.success('Ring settings saved successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save settings: ${error.message}`);
    },
  });

  return {
    settings,
    isLoading,
    updateSettings: updateSettings.mutate,
    isUpdating: updateSettings.isPending,
  };
}
